import { ChangeDetectorRef, DestroyRef, Directive, effect, input, OnDestroy, OnInit, Optional } from '@angular/core';
import { DatePickerComponent, DateTimePickerComponent } from '@progress/kendo-angular-dateinputs';
import { DropDownListComponent } from '@progress/kendo-angular-dropdowns';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { startWith } from 'rxjs';

const UNSET_VALUE: unknown = Symbol('ti-clear-input-button-unset');

@Directive({
  standalone: false,
  selector: '[tiClearInputButton]',
})
export class ClearDirective implements OnInit, OnDestroy {
  private button: HTMLElement | null = null;
  private clickHandler: any;
  private mouseOverHandler = () => {
    this.button.style.cursor = 'pointer';
  };

  enabled = input<boolean | ''>(true, { alias: 'tiClearInputButton' });
  hideOnClear = input<boolean>(false);
  tiClearInputButtonValue = input<unknown>(UNSET_VALUE);

  private readonly component: DatePickerComponent | DateTimePickerComponent | DropDownListComponent;

  constructor(
    @Optional() private dpc: DatePickerComponent,
    @Optional() private dtpc: DateTimePickerComponent,
    @Optional() private ddlc: DropDownListComponent,
    private cdr: ChangeDetectorRef,
    private destroyRef: DestroyRef,
  ) {
    this.component = dpc || dtpc || ddlc;

    effect(() => {
      const v = this.tiClearInputButtonValue();
      if (v === UNSET_VALUE) return;
      if (!this.component || this.enabled() === false || !this.hideOnClear()) return;

      const hasValue = Array.isArray(v) ? v.length > 0 : v != null && v !== '';
      if (hasValue && !this.buttonAppended) {
        this.appendButton();
      } else if (!hasValue && this.buttonAppended) {
        this.removeButton();
      }
    });
  }

  ngOnInit() {
    if (!this.component || this.enabled() === false) {
      return;
    }

    this.button = document.createElement('span');
    this.button.className = 'fa fa-times-circle k-clear-value';
    this.button.appendChild(document.createTextNode(''));
    let cdr = this.cdr;
    let component = this.component;
    let clickHandler = ($event) => {
      try {
        (component as any).control.control.markAsTouched();
      } catch (e) {}
      if ((component as DatePickerComponent).handleChange) {
        (component as DatePickerComponent).handleChange(null);
      }
      if ((component as DateTimePickerComponent).handleInputValueChange) {
        (component as DateTimePickerComponent).handleInputValueChange(null);
      }
      if ((component as DropDownListComponent).handleClick) {
        $event.stopPropagation();
      }

      if (component instanceof DropDownListComponent) {
        // dropdown needs additional logic
        component.formControl?.setValue(null);
      }

      component.writeValue(null);
      component.valueChange.emit(null);
      cdr.markForCheck();
    };

    this.clickHandler = clickHandler;

    this.button.addEventListener('click', clickHandler);
    this.button.addEventListener('mouseover', this.mouseOverHandler);

    if (this.hideOnClear() && this.tiClearInputButtonValue() === UNSET_VALUE) {
      component.valueChange
        .asObservable()
        .pipe(startWith(this.component.value), takeUntilDestroyed(this.destroyRef))
        .subscribe((value) => {
          if (value && !this.buttonAppended) {
            this.appendButton();
          }

          if (!value && this.buttonAppended) {
            this.removeButton();
          }
        });
    }
    !this.hideOnClear() && this.appendButton();
  }

  buttonAppended = false;

  appendButton() {
    //after upgrading to the latest version of kendo-datepicker element wrapper isn't initialized. setTimeout is workaround
    setTimeout(() => {
      if (this.component?.wrapper) {
        if (this.component === this.ddlc) {
          this.component.wrapper.nativeElement.querySelector('button').before(this.button);
          this.buttonAppended = true;
        } else {
          const wrapperEl = this.component.wrapper.nativeElement;
          const firstChild = wrapperEl.children[0];
          if (firstChild) {
            wrapperEl.insertBefore(this.button, firstChild.nextSibling);
          } else {
            wrapperEl.appendChild(this.button);
          }
          this.buttonAppended = true;
        }
      }
    }, 0);
  }

  private removeButton() {
    this.button?.remove();
    this.buttonAppended = false;
  }

  ngOnDestroy() {
    this.button?.removeEventListener('click', this.clickHandler);
    this.button?.removeEventListener('mouseover', this.mouseOverHandler);
  }
}
